import { useState, useRef, useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { Play, Pause, Volume2, Volume1, VolumeX, Loader2 } from '../../icons/wp-icons';

function formatTime(sec) {
  if (!sec || !isFinite(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function primaryAlpha(pct) {
  return `color-mix(in srgb, var(--primary) ${pct}%, transparent)`;
}

// Für Auth-geschützte Proxy-Endpunkte hängt der Browser den JWT nicht
// automatisch an Medien-Requests — wir hängen den Token als ?token= an,
// den unser Proxy in Authorization übersetzt.
function resolveAudioSrc(src) {
  if (!src) return src;
  if (!src.startsWith('/api/voice-assistant/') && !src.startsWith('/api/easybell/')) return src;
  try {
    const token = localStorage.getItem('token');
    if (!token) return src;
    const sep = src.includes('?') ? '&' : '?';
    return `${src}${sep}token=${encodeURIComponent(token)}`;
  } catch {
    return src;
  }
}

/**
 * Audio-Player mit Wellenform-Visualisierung (wavesurfer.js v7).
 *
 * Audio wird weiterhin über den Backend-Proxy von ElevenLabs gestreamt —
 * KEINE Audio-Dateien server-seitig gespeichert. wavesurfer lädt die Bytes
 * in einen Client-side AudioBuffer, berechnet Peaks und rendert eine
 * Canvas-Wellenform.
 *
 * Farbgebung:
 *   - abgespielter Teil   → solid `var(--primary)` (wie der Play-Button)
 *   - noch nicht abgespielt → gedämpftes `var(--primary)` mit ~20% Alpha
 *
 * Glitch-Vermeidung: `timeupdate` feuert sehr häufig, wir throttlen deshalb
 * den React-Re-Render via requestAnimationFrame. Der Fortschritt auf der
 * Wellenform wird von wavesurfer intern verwaltet — das bleibt smooth auch
 * wenn wir nur ~4x/Sekunde den Zeitstempel im Text aktualisieren.
 */
export default function AudioPlayer({ src }) {
  const resolvedSrc = resolveAudioSrc(src);
  const containerRef = useRef(null);
  const wavesurferRef = useRef(null);
  // Zeit-Anzeige direkt per DOM-Ref aktualisieren — kein React-State,
  // kein Re-Render pro Playback-Frame. Das war die Ursache für das
  // „glitchen" der Wellenform (jeder Re-Render → Re-Layout → wavesurfer
  // Canvas-Redraw).
  const currentTimeRef = useRef(null);
  const durationTextRef = useRef(null);
  // „Pop"-Glow, der an der Cursor-Position mitläuft und die gerade vom
  // Zeiger überfahrenen grauen Bars kurz in der Akzentfarbe aufleuchten
  // lässt (via mix-blend-mode: color auf einem primary-gefärbten Gradient).
  const progressGlowRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [speed, setSpeed] = useState(1);
  const [error, setError] = useState(false);

  const speeds = [1, 1.25, 1.5, 1.75, 2];

  useEffect(() => {
    if (!containerRef.current || !resolvedSrc) return;

    // Primary aus Computed-Style holen — kann hex, rgb() oder rgba() sein.
    // Mit einem Hidden-Div + getComputedStyle konvertieren wir jeden gültigen
    // CSS-Farbwert zu einem einheitlichen rgb(…)-String, den der Canvas-
    // Renderer garantiert parsen kann.
    const primaryRaw = getComputedStyle(containerRef.current).getPropertyValue('--primary').trim() || '#1e5fd9';
    const colorProbe = document.createElement('div');
    colorProbe.style.color = primaryRaw;
    colorProbe.style.display = 'none';
    document.body.appendChild(colorProbe);
    const primaryRgb = getComputedStyle(colorProbe).color; // z.B. "rgb(30, 95, 217)"
    document.body.removeChild(colorProbe);
    const rgbMatch = primaryRgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    const [r, g, b] = rgbMatch ? [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])] : [30, 95, 217];

    // Unplayed: neutrales Hellgrau (maximaler Kontrast zum played-Teil,
    // unabhängig von der Akzentfarbe). Played: solide Primary-Vollfarbe.
    // Zusammen ergibt das „grau links bevor, farbig hinter dem Cursor".
    const waveColor = 'rgba(160, 170, 180, 0.55)';
    const progressColor = `rgb(${r}, ${g}, ${b})`;
    const cursorColor = `rgba(${r}, ${g}, ${b}, 0.95)`;
    // eslint-disable-next-line no-console
    console.debug('[AudioPlayer] primary=', primaryRgb, 'waveColor=', waveColor, 'progressColor=', progressColor);

    const ws = WaveSurfer.create({
      container: containerRef.current,
      url: resolvedSrc,
      waveColor,
      progressColor,
      cursorColor,
      cursorWidth: 0,
      barWidth: 3,
      barGap: 2,
      height: 48,
      normalize: true,
      mediaControls: false,
      fillParent: true,
      interact: true,
      dragToSeek: true,
      fetchParams: { credentials: 'same-origin' },
    });
    wavesurferRef.current = ws;

    // Zeit-Text + Glow-Position direkt per DOM-Ref updaten — umgeht
    // React-Re-Renders während Playback komplett.
    const updateTimeText = (t) => {
      if (currentTimeRef.current) currentTimeRef.current.textContent = formatTime(t);
    };
    const updateGlowPosition = (t) => {
      const glow = progressGlowRef.current;
      const total = ws.getDuration?.() || 0;
      if (!glow || !total) return;
      const pct = Math.max(0, Math.min(100, (t / total) * 100));
      glow.style.left = `${pct}%`;
    };

    const unsubs = [
      ws.on('ready', (d) => {
        setDuration(d);
        setReady(true);
        setLoadingProgress(100);
        if (durationTextRef.current) durationTextRef.current.textContent = formatTime(d);
      }),
      ws.on('loading', (percent) => setLoadingProgress(percent)),
      ws.on('timeupdate', (t) => { updateTimeText(t); updateGlowPosition(t); }),
      ws.on('seeking', (t) => { updateTimeText(t); updateGlowPosition(t); }),
      ws.on('play', () => setPlaying(true)),
      ws.on('pause', () => setPlaying(false)),
      ws.on('finish', () => setPlaying(false)),
      ws.on('error', () => setError(true)),
    ];

    return () => {
      unsubs.forEach((fn) => typeof fn === 'function' && fn());
      try { ws.destroy(); } catch { /* ignore */ }
      wavesurferRef.current = null;
    };
  }, [resolvedSrc]);

  const toggle = () => {
    const ws = wavesurferRef.current;
    if (!ws || !ready) return;
    ws.playPause();
  };

  const cycleSpeed = () => {
    const next = speeds[(speeds.indexOf(speed) + 1) % speeds.length];
    setSpeed(next);
    const ws = wavesurferRef.current;
    if (ws) ws.setPlaybackRate(next, true);
  };

  const setVolumeFromClientX = (rect, clientX) => {
    const v = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setVolume(v);
    const ws = wavesurferRef.current;
    if (ws) ws.setVolume(v);
    if (v === 0) setMuted(true);
    else if (muted) setMuted(false);
  };

  // Pointer-Drag auf dem Lautstärke-Balken. setPointerCapture sorgt dafür,
  // dass der Drag weiterläuft, auch wenn der Finger/Cursor den Balken verlässt.
  const handleVolumePointerDown = (e) => {
    const bar = e.currentTarget;
    try { bar.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    const rect = bar.getBoundingClientRect();
    setVolumeFromClientX(rect, e.clientX);
  };
  const handleVolumePointerMove = (e) => {
    const bar = e.currentTarget;
    if (!bar.hasPointerCapture?.(e.pointerId)) return;
    const rect = bar.getBoundingClientRect();
    setVolumeFromClientX(rect, e.clientX);
  };
  const handleVolumePointerUp = (e) => {
    const bar = e.currentTarget;
    try { bar.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    const ws = wavesurferRef.current;
    if (ws) ws.setMuted(next);
  };

  const VolumeIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  if (error) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 text-sm text-text-muted">
        Aufzeichnung nicht verfügbar
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-primary-light px-3 py-2 space-y-2" style={{ border: `1px solid ${primaryAlpha(25)}` }}>
      {/* Row 1: Play + Waveform + Time */}
      <div className="flex items-center gap-2.5">
        <button
          onClick={toggle}
          disabled={!ready}
          className="flex-shrink-0 w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center hover:opacity-90 transition active:scale-95 disabled:opacity-50 disabled:cursor-wait"
          aria-label={playing ? 'Pause' : 'Abspielen'}
        >
          {!ready ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : playing ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4 ml-0.5" />
          )}
        </button>

        <div className="flex-1 relative">
          <div
            ref={containerRef}
            className="w-full cursor-pointer"
            style={{ minHeight: 48 }}
          />
          {/* „Pop"-Halo an der Cursor-Position: weicher Primary-Glow, der
              mit dem Abspiel-Cursor mitwandert und die gerade überfahrene
              Bar sichtbar hervorhebt. Liegt per `z-index: 5` ÜBER den
              wavesurfer-Canvases (die in deren Shadow-DOM z-index 2 haben),
              Position wird per DOM-Ref auf `timeupdate` aktualisiert —
              kein React-Re-Render. */}
          {ready && (
            <div
              ref={progressGlowRef}
              aria-hidden="true"
              className={`absolute top-0 bottom-0 pointer-events-none transition-opacity duration-150 ${playing ? 'opacity-100' : 'opacity-0'}`}
              style={{
                left: '0%',
                width: '32px',
                transform: 'translateX(-50%)',
                background: 'radial-gradient(circle at center, color-mix(in srgb, var(--primary) 85%, transparent) 0%, color-mix(in srgb, var(--primary) 40%, transparent) 35%, transparent 75%)',
                filter: 'blur(2px)',
                zIndex: 5,
                mixBlendMode: 'screen',
              }}
            />
          )}
          {!ready && loadingProgress < 100 && (
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none text-[11px] font-medium"
              style={{ color: primaryAlpha(70) }}
            >
              Wellenform wird geladen… {Math.round(loadingProgress)}%
            </div>
          )}
        </div>

        <span className="text-xs text-text-muted tabular-nums flex-shrink-0 min-w-[72px] text-right">
          <span ref={currentTimeRef}>0:00</span>/<span ref={durationTextRef}>{formatTime(duration)}</span>
        </span>
      </div>

      {/* Row 2: Speed + Volume */}
      <div className="flex items-center gap-2 pl-11">
        <button
          onClick={cycleSpeed}
          className="text-xs font-semibold rounded px-2 py-0.5 text-primary transition active:scale-95"
          style={{ backgroundColor: primaryAlpha(15) }}
        >
          {speed}x
        </button>
        <button
          onClick={toggleMute}
          className="flex-shrink-0 transition active:scale-95"
          style={{ color: primaryAlpha(70) }}
          aria-label={muted ? 'Ton einschalten' : 'Stumm'}
        >
          <VolumeIcon className="w-4 h-4" />
        </button>
        <div
          onPointerDown={handleVolumePointerDown}
          onPointerMove={handleVolumePointerMove}
          onPointerUp={handleVolumePointerUp}
          onPointerCancel={handleVolumePointerUp}
          className="w-20 h-2 rounded-full cursor-pointer relative group touch-none select-none"
          style={{ backgroundColor: primaryAlpha(35) }}
        >
          <div
            className="h-full bg-primary rounded-full"
            style={{ width: `${(muted ? 0 : volume) * 100}%` }}
          />
          {/* Knob als Sibling des Fill-Balkens, damit das Clamping auf die
              Track-Breite bezogen werden kann: left wird zwischen 0 und
              (100% - 12px) begrenzt, damit der 12px-Knob nie über den Rand
              hinausragt. */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full border-2 border-white shadow sm:opacity-0 sm:group-hover:opacity-100 transition pointer-events-none"
            style={{ left: `max(0px, min(calc(${(muted ? 0 : volume) * 100}% - 6px), calc(100% - 12px)))` }}
          />
        </div>
      </div>
    </div>
  );
}
